import { useCallback, useEffect, useRef, useState } from "react";
import { DAEMON_URL } from "../config";
import { fetchArtifact, dispatchDeploy, getDeployStatus } from "../api/deploy";
import { HttpError } from "../api/errors";
import { parseArtifact, buildMcaddonBlob } from "../lib/artifactZip";

type Props = {
  /** projects.json id of the currently selected project. */
  project: string;
  /** Optional prefill for the branch input; blank => daemon uses the project default. */
  defaultBranch?: string;
};

type State = "idle" | "picking" | "building" | "waiting" | "done" | "error";

const HELPER_DOWNLOAD_URL = `${DAEMON_URL}/helper/download`;
const CODE_KEY = "thunderay-helper-code";
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function MinecraftIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12" />
    </svg>
  );
}

export default function DeployButton({ project, defaultBranch }: Props) {
  const [state, setState] = useState<State>("idle");
  const [branch, setBranch] = useState<string>(defaultBranch ?? "");
  const [code, setCode] = useState<string>(() => {
    try { return localStorage.getItem(CODE_KEY) ?? ""; } catch { return ""; }
  });
  const [message, setMessage] = useState<string>("");
  const [detail, setDetail] = useState<string>("");
  const runId = useRef(0);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (resetTimer.current) clearTimeout(resetTimer.current); }, []);

  const busy = state === "building" || state === "waiting";

  const scheduleReset = useCallback(() => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState("idle"), 8000);
  }, []);

  const saveCode = useCallback((v: string) => {
    const cleaned = v.trim().toUpperCase();
    setCode(cleaned);
    try { localStorage.setItem(CODE_KEY, cleaned); } catch { /* ignore */ }
  }, []);

  const downloadMcaddon = useCallback(async () => {
    try {
      const buf = await fetchArtifact(project, branch);
      const parsed = parseArtifact(buf);
      const blob = buildMcaddonBlob(parsed);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${parsed.manifest.deployFolderName}.mcaddon`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setMessage((e as Error).message || "Failed to build the .mcaddon.");
      setState("error");
    }
  }, [project, branch]);

  const run = useCallback(async () => {
    const myRun = ++runId.current;
    setMessage("");
    setDetail("");

    if (!/^[A-Za-z0-9-]{6,32}$/.test(code)) {
      setMessage("Enter the pairing code shown in the helper window.");
      setState("error");
      return;
    }

    setState("building");
    let jobId: string;
    try {
      jobId = await dispatchDeploy(code, project, branch);
    } catch (e) {
      setMessage(
        e instanceof HttpError
          ? e.status === 403 ? "You do not have access to deploy this project."
            : e.status === 401 ? "Your session expired — please sign in again."
              : e.message
          : (e as Error).message,
      );
      setState("error");
      return;
    }

    setState("waiting");
    const start = Date.now();
    let readySince = 0;
    while (Date.now() - start < 180_000) {
      await sleep(1500);
      if (runId.current !== myRun) return; // superseded / unmounted
      let st;
      try {
        st = await getDeployStatus(jobId);
      } catch {
        continue; // transient; keep polling
      }
      if (st.status === "done") {
        setMessage(`Deployed ${st.folder ?? ""}`);
        setDetail(`${st.fileCount ?? 0} files${st.commitSha ? ` · commit ${st.commitSha}` : ""} · re-enter your world or /reload`);
        setState("done");
        scheduleReset();
        return;
      }
      if (st.status === "error") {
        setMessage(st.error ?? "The build or write failed.");
        setState("error");
        return;
      }
      if (st.status === "building") {
        setDetail("Building on the server…");
        readySince = 0;
      } else if (st.status === "delivered") {
        setDetail("Helper is writing the pack…");
        readySince = 0;
      } else if (st.status === "ready") {
        setDetail("Waiting for the helper to pick it up…");
        if (!readySince) readySince = Date.now();
        else if (Date.now() - readySince > 12_000) {
          setMessage(`The helper did not pick this up. Make sure it is running and its pairing code is ${code}.`);
          setState("error");
          return;
        }
      }
    }
    setMessage("Timed out waiting for the helper.");
    setState("error");
  }, [project, branch, code, scheduleReset]);

  const triggerLabel = state === "building" ? "Building…" : state === "waiting" ? "Deploying…" : "Deploy to Minecraft";

  return (
    <div className="deploy-wrap">
      <button
        type="button"
        className={`deploy-btn${busy ? " deploy-btn-busy" : ""}`}
        disabled={busy}
        aria-busy={busy}
        onClick={() => setState(state === "picking" ? "idle" : "picking")}
        title="Build this branch and write it into your Minecraft dev folders via the local helper"
      >
        {busy ? <span className="deploy-spinner" aria-hidden="true" /> : <MinecraftIcon />}
        {triggerLabel}
      </button>

      {state === "picking" && (
        <div className="deploy-popover" role="dialog" aria-label="Deploy to Minecraft">
          <label className="deploy-field">
            <span>Helper code</span>
            <input
              type="text"
              value={code}
              placeholder="paste the code from the helper window"
              spellCheck={false}
              autoFocus={!code}
              onChange={(e) => saveCode(e.target.value)}
            />
          </label>
          <label className="deploy-field">
            <span>Branch</span>
            <input
              type="text"
              value={branch}
              placeholder="default branch"
              spellCheck={false}
              onChange={(e) => setBranch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void run();
                if (e.key === "Escape") setState("idle");
              }}
            />
          </label>
          <div className="deploy-actions">
            <button type="button" className="deploy-btn deploy-btn-primary" onClick={() => void run()}>
              <MinecraftIcon />
              Deploy to Minecraft
            </button>
            <button type="button" className="deploy-cancel" onClick={() => setState("idle")}>
              Cancel
            </button>
          </div>
          <p className="deploy-hint">
            Run the ThunderAy Deploy Helper and paste the pairing code it shows. Leave branch blank for the project default.
          </p>
          <a className="deploy-link" href={HELPER_DOWNLOAD_URL} download>
            Download helper
          </a>
          <button
            type="button"
            className="deploy-link"
            onClick={() => {
              setState("idle");
              window.dispatchEvent(new CustomEvent("thunderay-ai:open-builds"));
            }}
          >
            No helper (iPad)? Get the .mcaddon on the Builds page
          </button>
        </div>
      )}

      {(state === "building" || state === "waiting") && (
        <div className="deploy-popover" role="status">
          <strong>{state === "building" ? "Building…" : "Deploying…"}</strong>
          {detail && <span className="deploy-hint">{detail}</span>}
        </div>
      )}

      {state === "done" && (
        <div className="deploy-popover deploy-result-ok" role="status">
          <strong>✓ {message}</strong>
          {detail && <span className="deploy-hint">{detail}</span>}
        </div>
      )}

      {state === "error" && (
        <div className="deploy-popover deploy-result-error" role="alert">
          <span>{message}</span>
          <div className="deploy-actions">
            <button type="button" className="deploy-btn deploy-btn-primary" onClick={() => setState("picking")}>
              Back
            </button>
            <button type="button" className="deploy-cancel" onClick={() => setState("idle")}>
              Dismiss
            </button>
          </div>
          <button type="button" className="deploy-link" onClick={() => void downloadMcaddon()}>
            Or download .mcaddon to import manually
          </button>
        </div>
      )}
    </div>
  );
}
